import { useCallback, useMemo, useRef, useState } from 'react';
import { fetchWhat3wordsLocation } from '@rsos/reduxDataStore/src/features/what3words/what3wordsAPI';
import type { PlaceCandidate } from './GlobalMapSearch.types';

interface What3WordsApiResponse {
  coordinates?: { lat: number; lng: number };
  words?: string;
  nearestPlace?: string;
  country?: string;
}

const fetchW3W = fetchWhat3wordsLocation as unknown as (
  w3w: string,
) => Promise<What3WordsApiResponse>;

/**
 * Strict `word.word.word` shape: exactly two periods between three
 * letters-only tokens, no whitespace inside the phrase. The leading
 * `///` is optional and may be followed by whitespace, but the phrase
 * itself must be unambiguous.
 *
 * Why so strict? The legacy `w3wInputHelper` treats spaces and dots
 * as interchangeable separators, so terms like `"chuck e cheese"` or
 * `"chuck e. cheese"` get hijacked into a what3words lookup before
 * the regular search ever runs — masking real place / parcel /
 * caller-name matches behind a misfiring W3W shortcut. Requiring two
 * literal periods makes the intent explicit.
 */
const W3W_STRICT_PATTERN = /^(?:\/\/\/)?\s*([a-z]+\.[a-z]+\.[a-z]+)$/i;

/**
 * Parse a search term as a what3words address. Returns the normalized
 * lowercase `word.word.word` string, or `null` if the term doesn't
 * match the strict shape described on `W3W_STRICT_PATTERN`.
 */
export function parseWhat3WordsTerm(term: string): string | null {
  const trimmed = term.trim();
  if (!trimmed) return null;
  const match = W3W_STRICT_PATTERN.exec(trimmed);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Build a `PlaceCandidate` (the same shape the GIS geocoder produces)
 * from a W3W lookup result so the hit renders as an ordinary place row
 * in the dropdown and pins identically on click. Returns `null` when
 * the lookup didn't resolve to coordinates — the caller treats that as
 * "no result" and renders no row.
 */
function toPlaceCandidate(
  w3w: string,
  response: What3WordsApiResponse,
): PlaceCandidate | null {
  const lat = response.coordinates?.lat;
  const lng = response.coordinates?.lng;
  if (lat == null || lng == null) return null;
  const placeName = `///${response.words || w3w}`;
  const nearest = response.nearestPlace || '';
  return {
    address: nearest ? `${placeName}, ${nearest}` : placeName,
    location: { x: lng, y: lat },
    attributes: {
      PlaceName: placeName,
      ShortLabel: placeName,
      Place_addr: nearest,
      City: '',
      RegionAbbr: '',
      Postal: '',
      Region: '',
      Country: response.country || '',
      LongLabel: nearest ? `${placeName} · ${nearest}` : placeName,
      Addr_type: 'what3words',
      Loc_name: 'what3words',
    },
  };
}

/**
 * Background-lookup state for the most recent W3W phrase. `success`
 * carries the resolved place; `empty` means the lookup landed but
 * produced no coordinates (so nothing should render).
 */
export type W3WLookupState =
  | { status: 'pending'; phrase: string }
  | { status: 'success'; phrase: string; place: PlaceCandidate }
  | { status: 'empty'; phrase: string };

interface UseWhat3WordsSearch {
  /** See {@link parseWhat3WordsTerm}. */
  parse: (term: string) => string | null;
  /**
   * Latest lookup state. Consumers inject a dropdown row only when
   * `status === 'success'` and surface the pending flag in the combined
   * "searching" indicator. A miss (`empty`) renders no row.
   */
  lookup: W3WLookupState | null;
  /**
   * Fire a background lookup for the given (already-parsed) phrase. Does
   * NOT teleport the map — the result surfaces as a dropdown row the user
   * clicks to pin, like any other place. Idempotent for a phrase that's
   * already pending/resolved.
   */
  kickoff: (w3w: string) => void;
  /** Clear the lookup so its row disappears (e.g. input edited away). */
  reset: () => void;
}

/**
 * Background-fetch wrapper around the what3words coordinate lookup.
 * Modeled on `useMedicAlertSearch`: a kickoff stamps a fresh request,
 * the async fetch resolves into local state, and stale responses are
 * dropped via a monotonic request id so a fast follow-up wins.
 */
export default function useWhat3WordsSearch(): UseWhat3WordsSearch {
  const [lookup, setLookup] = useState<W3WLookupState | null>(null);
  const requestIdRef = useRef(0);
  const activePhraseRef = useRef<string | null>(null);

  const kickoff = useCallback((w3w: string) => {
    // Already chasing (or done with) this exact phrase — don't re-fetch
    // on every debounced keystroke for an unchanged W3W term.
    if (activePhraseRef.current === w3w) return;
    activePhraseRef.current = w3w;
    const requestId = ++requestIdRef.current;
    setLookup({ status: 'pending', phrase: w3w });
    void (async () => {
      try {
        const response = await fetchW3W(w3w);
        if (requestIdRef.current !== requestId) return;
        const place = toPlaceCandidate(w3w, response);
        setLookup(
          place
            ? { status: 'success', phrase: w3w, place }
            : { status: 'empty', phrase: w3w },
        );
      } catch {
        if (requestIdRef.current !== requestId) return;
        // A bad / unknown phrase is just "no result": render no row,
        // matching the regular search's empty behavior.
        setLookup({ status: 'empty', phrase: w3w });
      }
    })();
  }, []);

  const reset = useCallback(() => {
    // Bump the request id so any in-flight fetch is ignored on return.
    requestIdRef.current += 1;
    activePhraseRef.current = null;
    setLookup(null);
  }, []);

  return useMemo(
    () => ({ parse: parseWhat3WordsTerm, lookup, kickoff, reset }),
    [lookup, kickoff, reset],
  );
}
